import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";

// Create candidate
export const createCandidate = createAsyncThunk(
  "candidate/create",
  async (candidateData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${BASE_URL}/v1/candidate`,
        candidateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchCandidatesByAny = createAsyncThunk(
  "candidate/fetchByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${BASE_URL}/v1/candidate/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update candidate
export const updateCandidate = createAsyncThunk(
  "candidate/update",
  async (candidateData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await axios.patch(
        `${BASE_URL}/v1/candidate`,
        candidateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Delete candidate
export const deleteCandidate = createAsyncThunk(
  "candidate/delete",
  async (candidateIds, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await axios({
        method: "delete",
        url: `${BASE_URL}/v1/candidate`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: candidateIds,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchAllCandidates = createAsyncThunk(
  "candidate/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(`${BASE_URL}/v1/candidate`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  candidates: [],
  currentCandidate: null,
  isLoading: false,
  error: null,
  success: false,
  total: 0,
  lastFetchTime: null,
  isOfflineData: false,
};

const candidateSlice = createSlice({
  name: "candidate",
  initialState,
  reducers: {
    resetCandidateState: () => initialState,
    updateCandidateForm: (state, action) => {
      state.currentCandidate = {
        ...state.currentCandidate,
        ...action.payload,
      };
    },
    setOfflineMode: (state, action) => {
      state.isOfflineData = action.payload;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Create candidate cases
      .addCase(createCandidate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCandidate.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch candidates cases
      .addCase(fetchCandidatesByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCandidatesByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates = action.payload.data;
        state.total = action.payload.data.length;
        state.error = null;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchCandidatesByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update candidate cases
      .addCase(updateCandidate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCandidate.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete candidate cases
      .addCase(deleteCandidate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCandidate.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllCandidates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates = action.payload.data;
        state.total = action.payload.data.length;
        state.error = null;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchAllCandidates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetCandidateState,
  updateCandidateForm,
  setOfflineMode,
  loadCachedData,
} = candidateSlice.actions;

// Selectors
export const selectCandidates = (state) => state.candidate.candidates;
export const selectCurrentCandidate = (state) =>
  state.candidate.currentCandidate;
export const selectCandidateLoading = (state) => state.candidate.isLoading;
export const selectCandidateError = (state) => state.candidate.error;
export const selectCandidateSuccess = (state) => state.candidate.success;
export const selectCandidateTotal = (state) => state.candidate.total;
export const selectIsOfflineData = (state) =>
  state.candidate.isOfflineData || false;

export default candidateSlice.reducer;
